import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Crown, KeyRound, Pencil, Plus, UserCog } from "lucide-react";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { PERMISSION_OPTIONS, PRESET_LABELS, type Permission } from "@/lib/property-access";
import { statusClass } from "@/lib/status";
import { useAppStore } from "@/store/app-store";
import { Button } from "@/components/ui/Button";
import { LoadingGrid, StateView } from "@/components/ui/StateView";

type Member = {
  id: string; userId: string; name: string; email: string; phone: string | null; accountStatus: string; pendingFirstLogin: boolean;
  preset: string; permissions: Permission[]; status: "ACTIVE" | "DISABLED"; addedAt: string;
  maxDiscountPercent: number | null; maxDiscountAmount: number | null;
};
type TeamData = {
  propertyId: string; propertyName: string;
  mainOwner: { userId: string; name: string; email: string; phone: string | null };
  members: Member[];
  canManage: boolean;
  presets: Record<string, Permission[]>;
};
type Confirm = { memberId: string; title: string; note: string; label: string; danger?: boolean; typeToConfirm?: string; run: () => void };

const invalidateTeam = () => Promise.all([
  queryClient.invalidateQueries({ queryKey: ["team"] }),
  queryClient.invalidateQueries({ queryKey: ["property-access"] }),
]);

// Who works on this property. Everyone with access sees the team; the main owner (or an admin) adds people,
// picks what each can do (presets or individual permissions), edits, switches off, removes and hands over.
export function TeamPage() {
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [editingOwner, setEditingOwner] = useState(false);
  const [secret, setSecret] = useState<{ name: string; email: string; password: string } | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const query = useQuery({ queryKey: ["team", propertyId], queryFn: () => api<TeamData>(`/members?propertyId=${propertyId}`) });
  const action = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => { setConfirm(null); await invalidateTeam(); },
  });

  if (query.isLoading) return <main className="page"><LoadingGrid /></main>;
  if (query.isError || !query.data) return <main className="page"><StateView title="Couldn't load the team" message="Check your internet connection and try again." action={() => void query.refetch()} /></main>;
  const team = query.data;
  const ask = (next: Confirm) => { action.reset(); setConfirm(next); };

  return (
    <main className="page team-page">
      <header className="page-head resource-head">
        <div>
          <h1>Team & access</h1>
          <p className="page-head__meta">{team.propertyName} · {team.members.length + 1} people</p>
        </div>
        {team.canManage && !adding && <Button onClick={() => { setAdding(true); setEditing(null); }}><Plus size={18} /> Add person</Button>}
      </header>

      {secret && (
        <section className="card team-secret" role="status">
          <KeyRound size={20} aria-hidden="true" />
          <div>
            <strong>Share these login details with {secret.name}</strong>
            <p>Email: <b>{secret.email}</b> · Temporary password: <b className="num">{secret.password}</b></p>
            <small>Shown only once. They must set their own password at first login.</small>
          </div>
          <Button variant="secondary" onClick={() => { void navigator.clipboard?.writeText(`${secret.email} / ${secret.password}`); }}>Copy</Button>
          <Button variant="ghost" onClick={() => setSecret(null)}>Done</Button>
        </section>
      )}

      {adding && (
        <MemberForm
          presets={team.presets}
          onCancel={() => setAdding(false)}
          onSaved={async (result: { member: Member; temporaryPassword: string | null }) => {
            setAdding(false);
            if (result.temporaryPassword) setSecret({ name: result.member.name, email: result.member.email, password: result.temporaryPassword });
            await invalidateTeam();
          }}
          submit={(input) => api<{ member: Member; temporaryPassword: string | null }>("/members", { method: "POST", body: { propertyId, ...input } })}
        />
      )}

      <section className="card team-card">
        <div className="team-row">
          <span className="team-row__icon"><Crown size={18} aria-hidden="true" /></span>
          <div className="team-row__main">
            <strong>{team.mainOwner.name}</strong>
            <small>{team.mainOwner.email}{team.mainOwner.phone ? ` · ${team.mainOwner.phone}` : ""}</small>
          </div>
          <span className="status status--confirmed">Main owner · all access</span>
          {team.canManage && <Button variant="ghost" aria-label="Edit main owner" onClick={() => setEditingOwner((open) => !open)}><Pencil size={16} /></Button>}
        </div>
        {editingOwner && (
          <OwnerDetailsForm
            name={team.mainOwner.name}
            phone={team.mainOwner.phone ?? ""}
            onCancel={() => setEditingOwner(false)}
            submit={async (input) => {
              await api(`/members/main-owner/details`, { method: "PATCH", body: { propertyId, ...input } });
              setEditingOwner(false);
              await invalidateTeam();
            }}
          />
        )}

        {team.members.map((member) => (
          <div key={member.id} className="team-member">
            <div className="team-row">
              <span className="team-row__icon"><UserCog size={18} aria-hidden="true" /></span>
              <div className="team-row__main">
                <strong>{member.name}</strong>
                <small>{member.email}{member.phone ? ` · ${member.phone}` : ""}{member.pendingFirstLogin ? " · hasn't logged in yet" : ""}</small>
              </div>
              <span className={member.status === "ACTIVE" ? "status status--confirmed" : statusClass("CANCELLED")}>{member.status === "ACTIVE" ? PRESET_LABELS[member.preset] ?? member.preset : "Access off"}</span>
            </div>
            <ul className="team-permissions">
              {PERMISSION_OPTIONS.filter((option) => member.permissions.includes(option.key)).map((option) => <li key={option.key}>{option.label}</li>)}
              {member.permissions.includes("DISCOUNTS") && (member.maxDiscountPercent != null || member.maxDiscountAmount != null) && (
                <li>Discount limit: {[member.maxDiscountPercent != null ? `${member.maxDiscountPercent}%` : "", member.maxDiscountAmount != null ? `Rs ${member.maxDiscountAmount}` : ""].filter(Boolean).join(" / ")}</li>
              )}
            </ul>
            {team.canManage && editing?.id !== member.id && (
              <div className="team-actions">
                <Button variant="secondary" onClick={() => { setEditing(member); setAdding(false); }}><Pencil size={15} /> Edit</Button>
                <Button variant="secondary" onClick={() => ask({
                  memberId: member.id,
                  title: member.status === "ACTIVE" ? `Switch off ${member.name}'s access?` : `Switch ${member.name}'s access back on?`,
                  note: member.status === "ACTIVE" ? "They can't open this property until you switch it back on. Nothing they did is changed." : "They get their permissions back.",
                  label: member.status === "ACTIVE" ? "Yes, switch off" : "Yes, switch on",
                  danger: member.status === "ACTIVE",
                  run: () => action.mutate(() => api(`/members/${member.id}`, { method: "PATCH", body: { status: member.status === "ACTIVE" ? "DISABLED" : "ACTIVE" } })),
                })}>{member.status === "ACTIVE" ? "Switch off" : "Switch on"}</Button>
                {member.pendingFirstLogin && (
                  <Button variant="secondary" onClick={() => ask({
                  memberId: member.id,
                    title: `New temporary password for ${member.name}?`, note: "The old temporary password stops working.", label: "Yes, new password",
                    run: () => action.mutate(async () => {
                      const result = await api<{ temporaryPassword: string }>(`/members/${member.id}/reset-password`, { method: "POST" });
                      setSecret({ name: member.name, email: member.email, password: result.temporaryPassword });
                    }),
                  })}><KeyRound size={15} /> New password</Button>
                )}
                {member.status === "ACTIVE" && (
                  <Button variant="secondary" onClick={() => ask({
                  memberId: member.id,
                    title: `Make ${member.name} the main owner?`, danger: true, typeToConfirm: "OWNER", label: "Yes, hand over",
                    note: `${member.name} gets every permission and manages the team. ${team.mainOwner.name} stays on with full access but can no longer manage the team.`,
                    run: () => action.mutate(() => api(`/members/${member.id}/make-main-owner`, { method: "POST" })),
                  })}><Crown size={15} /> Make main owner</Button>
                )}
                <Button variant="danger" onClick={() => ask({
                  memberId: member.id,
                  title: `Remove ${member.name} from ${team.propertyName}?`, danger: true, typeToConfirm: "REMOVE", label: "Yes, remove",
                  note: "Their access ends now. Everything they did stays in the booking history with their name.",
                  run: () => action.mutate(() => api(`/members/${member.id}`, { method: "DELETE" })),
                })}>Remove</Button>
              </div>
            )}
            {editing?.id === member.id && (
              <MemberForm
                member={member}
                presets={team.presets}
                onCancel={() => setEditing(null)}
                onSaved={async () => { setEditing(null); await invalidateTeam(); }}
                submit={(input) => api(`/members/${member.id}`, { method: "PATCH", body: input })}
              />
            )}
            {confirm?.memberId === member.id && <ConfirmBox confirm={confirm} busy={action.isPending} error={action.error} onCancel={() => setConfirm(null)} />}
          </div>
        ))}
        {!team.members.length && <p className="team-empty">Only the main owner works on this property.{team.canManage ? " Add people with Add person." : ""}</p>}
      </section>
    </main>
  );
}

function ConfirmBox({ confirm, busy, error, onCancel }: { confirm: Confirm; busy: boolean; error: Error | null; onCancel: () => void }) {
  const [typed, setTyped] = useState("");
  const ready = !confirm.typeToConfirm || typed.trim().toUpperCase() === confirm.typeToConfirm;
  return (
    <div className="bd-confirm" role="alertdialog">
      <p><strong>{confirm.title}</strong> {confirm.note}</p>
      {confirm.typeToConfirm && (
        <label className="bd-confirm__type">Type <b>{confirm.typeToConfirm}</b> to confirm<input value={typed} autoComplete="off" onChange={(event) => setTyped(event.target.value)} /></label>
      )}
      {error && <div className="form-error">{error.message}</div>}
      <div className="bd-actions__row">
        <Button variant="secondary" disabled={busy} onClick={onCancel}>Go back</Button>
        <Button variant={confirm.danger ? "danger" : "primary"} loading={busy} disabled={!ready} onClick={confirm.run}>{confirm.label}</Button>
      </div>
    </div>
  );
}

type MemberInput = { name?: string; email?: string; phone?: string; permissions: Permission[]; maxDiscountPercent?: number | null; maxDiscountAmount?: number | null };

// Add or edit a person: contact details plus a preset or hand-picked permissions. Saving asks once more.
function MemberForm<T>({ member, presets, onCancel, onSaved, submit }: {
  member?: Member;
  presets: Record<string, Permission[]>;
  onCancel: () => void;
  onSaved: (result: T) => void | Promise<void>;
  submit: (input: MemberInput) => Promise<T>;
}) {
  const [name, setName] = useState(member?.name ?? "");
  const [email, setEmail] = useState(member?.email ?? "");
  const [phone, setPhone] = useState(member?.phone ?? "");
  const [permissions, setPermissions] = useState<Permission[]>(member?.permissions ?? presets.FRONT_DESK ?? ["BOOKINGS_VIEW"]);
  // Discount limit (empty = no limit); above it the main owner approves.
  const [limitPercent, setLimitPercent] = useState(member?.maxDiscountPercent != null ? String(member.maxDiscountPercent) : "");
  const [limitAmount, setLimitAmount] = useState(member?.maxDiscountAmount != null ? String(member.maxDiscountAmount) : "");
  const [confirming, setConfirming] = useState(false);
  const save = useMutation({
    mutationFn: () => submit({
      name: name.trim(), email: email.trim(), phone: phone.trim(), permissions,
      maxDiscountPercent: limitPercent === "" ? null : Number(limitPercent),
      maxDiscountAmount: limitAmount === "" ? null : Number(limitAmount),
    }),
    onSuccess: (result) => onSaved(result),
  });
  const presetKey = Object.keys(presets).find((key) => presets[key].length === permissions.length && presets[key].every((permission) => permissions.includes(permission))) ?? "CUSTOM";
  const toggle = (key: Permission) => {
    const next = permissions.includes(key) ? permissions.filter((item) => item !== key) : [...permissions, key];
    // Every permission includes seeing the bookings.
    setPermissions(next.length && !next.includes("BOOKINGS_VIEW") ? [...next, "BOOKINGS_VIEW"] : next.length ? next : ["BOOKINGS_VIEW"]);
  };
  const valid = name.trim().length >= 2 && /\S+@\S+\.\S+/.test(email) && /^(?:\d{7,10}|\+\d{8,14})$/.test(phone.trim()) && permissions.length > 0;

  return (
    <section className="card booking-inline-form team-form">
      <h4>{member ? `Edit ${member.name}` : "Add a person"}</h4>
      <div className="property-form-grid">
        <label>Name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Phone<input value={phone} inputMode="tel" onChange={(event) => setPhone(event.target.value)} /></label>
        <label className="property-form-grid__wide">Email (used to log in)<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      </div>
      {!member && <div className="form-hint">If this email or phone already has an account, it is linked - no new password.</div>}
      <div className="team-presets" role="group" aria-label="Preset">
        {Object.keys(presets).map((key) => (
          <button key={key} type="button" className={key === presetKey ? "filter-tab filter-tab--active" : "filter-tab"} onClick={() => setPermissions([...presets[key]])}>{PRESET_LABELS[key] ?? key}</button>
        ))}
        {presetKey === "CUSTOM" && <span className="filter-tab filter-tab--active">Custom</span>}
      </div>
      <div className="team-permission-list">
        {PERMISSION_OPTIONS.map((option) => (
          <label key={option.key} className="checkbox-row">
            <input type="checkbox" checked={permissions.includes(option.key)} disabled={option.key === "BOOKINGS_VIEW"} onChange={() => toggle(option.key)} />
            <span><strong>{option.label}</strong><small>{option.hint}</small></span>
          </label>
        ))}
      </div>
      {permissions.includes("DISCOUNTS") && (
        <div className="property-form-grid">
          <label>Max discount % per booking<input type="number" min={0} max={100} step="0.01" placeholder="No limit" value={limitPercent} onChange={(event) => setLimitPercent(event.target.value)} /></label>
          <label>Max discount ₹ per booking<input type="number" min={0} step="1" placeholder="No limit" value={limitAmount} onChange={(event) => setLimitAmount(event.target.value)} /></label>
          <small className="field-hint property-form-grid__wide">A bigger discount is sent to the main owner, who approves or rejects it. Leave empty for no limit.</small>
        </div>
      )}
      {save.error && <div className="form-error">{save.error.message}</div>}
      {confirming ? (
        <div className="bd-confirm" role="alertdialog">
          <p><strong>{member ? "Save these changes?" : `Give ${name.trim()} access?`}</strong> {PRESET_LABELS[presetKey]} · {permissions.length} permission(s).</p>
          <div className="bd-actions__row">
            <Button variant="secondary" disabled={save.isPending} onClick={() => setConfirming(false)}>Go back</Button>
            <Button loading={save.isPending} onClick={() => save.mutate(undefined, { onSettled: () => setConfirming(false) })}>{member ? "Yes, save" : "Yes, give access"}</Button>
          </div>
        </div>
      ) : (
        <div className="booking-inline-form__actions">
          <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button type="button" disabled={!valid} onClick={() => setConfirming(true)}>Continue</Button>
        </div>
      )}
    </section>
  );
}

function OwnerDetailsForm({ name: initialName, phone: initialPhone, onCancel, submit }: { name: string; phone: string; onCancel: () => void; submit: (input: { name: string; phone: string }) => Promise<void> }) {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(initialPhone);
  const save = useMutation({ mutationFn: () => submit({ name: name.trim(), phone: phone.trim() }) });
  const valid = name.trim().length >= 2 && /^(?:\d{7,10}|\+\d{8,14})$/.test(phone.trim());
  return (
    <section className="booking-inline-form team-form">
      <h4>Edit main owner</h4>
      <div className="property-form-grid">
        <label>Name<input value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Phone<input value={phone} inputMode="tel" onChange={(event) => setPhone(event.target.value)} /></label>
      </div>
      <div className="form-hint">The main owner's email is changed from their own profile.</div>
      {save.error && <div className="form-error">{save.error.message}</div>}
      <div className="booking-inline-form__actions">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="button" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>Save</Button>
      </div>
    </section>
  );
}
